import { useEffect, useState } from "react";
import { PageLayout } from "../components/Layout";
import { useAuth, API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Save, Printer, Rocket, CheckCircle2 } from "lucide-react";

const CATEGORY_COLORS = {
  "Communication": "from-sky-100 to-sky-50 border-sky-200",
  "Focus & Environment": "from-indigo-100 to-indigo-50 border-indigo-200",
  "Feedback & Support": "from-emerald-100 to-emerald-50 border-emerald-200",
  "Sensory Needs": "from-rose-100 to-rose-50 border-rose-200",
  "Collaboration": "from-amber-100 to-amber-50 border-amber-200",
  "Strengths": "from-purple-100 to-purple-50 border-purple-200",
};

const LikertScale = ({ value, onChange, questionId }) => (
  <div className="flex items-center gap-2" data-testid={`likert-${questionId}`}>
    <span className="text-xs text-slate-500 w-24">Not at all</span>
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(n)}
        className={`w-10 h-10 rounded-full border-2 font-semibold transition-all ${
          value === n
            ? "bg-[#1E3A8A] text-white border-[#1E3A8A] scale-110"
            : "bg-white text-slate-600 border-slate-200 hover:border-[#1E3A8A]/50"
        }`}
        data-testid={`likert-${questionId}-${n}`}
      >
        {n}
      </button>
    ))}
    <span className="text-xs text-slate-500 w-28 text-right">Significantly</span>
  </div>
);

const McqOption = ({ option, selected, onChange, testid }) => (
  <button
    type="button"
    onClick={onChange}
    className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
      selected
        ? "bg-[#FFD507]/10 border-[#FFD507] text-slate-900 font-medium shadow-sm"
        : "bg-white border-slate-200 text-slate-600 hover:border-[#FFD507]/40"
    }`}
    data-testid={testid}
  >
    <div className="flex items-center gap-3">
      <span
        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
          selected ? "border-[#FFD507] bg-[#FFD507]" : "border-slate-300"
        }`}
      >
        {selected && <span className="w-2 h-2 rounded-full bg-white" />}
      </span>
      {option}
    </div>
  </button>
);

const MultiOption = ({ option, selected, onChange, testid }) => (
  <button
    type="button"
    onClick={onChange}
    className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
      selected
        ? "bg-[#1E3A8A]/10 border-[#1E3A8A] text-slate-900 font-medium shadow-sm"
        : "bg-white border-slate-200 text-slate-600 hover:border-[#1E3A8A]/40"
    }`}
    data-testid={testid}
  >
    <div className="flex items-center gap-3">
      <span
        className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 ${
          selected ? "border-[#1E3A8A] bg-[#1E3A8A]" : "border-slate-300"
        }`}
      >
        {selected && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
      </span>
      {option}
    </div>
  </button>
);

const WorkingStyleQuiz = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [answersMap, setAnswersMap] = useState({}); // {question_id: value}
  const [sharePublicly, setSharePublicly] = useState(false);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [qRes, rRes] = await Promise.all([
          fetch(`${API}/working-style/questions`, { credentials: "include" }),
          fetch(`${API}/working-style/response/me`, { credentials: "include" }),
        ]);
        if (qRes.ok) {
          const qData = await qRes.json();
          setCategories(qData.categories);
          setQuestions(qData.questions);
        }
        if (rRes.ok) {
          const rData = await rRes.json();
          const map = {};
          (rData.answers || []).forEach((a) => {
            map[a.question_id] = a.answer;
          });
          setAnswersMap(map);
          setSharePublicly(!!rData.share_publicly);
          setCompleted(!!rData.completed);
        }
      } catch {
        toast.error("Failed to load quiz");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const setAnswer = (qid, value) => {
    setAnswersMap((prev) => ({ ...prev, [qid]: value }));
  };

  const toggleMulti = (qid, option) => {
    setAnswersMap((prev) => {
      const cur = Array.isArray(prev[qid]) ? prev[qid] : [];
      const next = cur.includes(option) ? cur.filter((o) => o !== option) : [...cur, option];
      return { ...prev, [qid]: next };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const answers = questions
        .map((q) => ({ question_id: q.question_id, answer: answersMap[q.question_id] }))
        .filter((a) => a.answer !== undefined && a.answer !== null && a.answer !== "" && !(Array.isArray(a.answer) && a.answer.length === 0));
      const res = await fetch(`${API}/working-style/response`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ answers }),
      });
      if (res.ok) {
        toast.success("Your Working Style profile was saved!");
        setCompleted(true);
      } else {
        toast.error("Failed to save responses");
      }
    } catch {
      toast.error("An error occurred saving your responses");
    } finally {
      setSaving(false);
    }
  };

  const handleShareToggle = async (checked) => {
    if (!completed) {
      toast.error("Complete and save the quiz first");
      return;
    }
    setSharePublicly(checked);
    try {
      const res = await fetch(`${API}/working-style/share?share_publicly=${checked}`, {
        method: "PUT",
        credentials: "include",
      });
      if (!res.ok) {
        toast.error("Failed to update sharing");
        setSharePublicly(!checked);
      } else {
        toast.success(checked ? "Card is now visible on your Talent DB profile" : "Card hidden from Talent DB profile");
      }
    } catch {
      toast.error("An error occurred");
      setSharePublicly(!checked);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const answeredCount = questions.filter((q) => {
    const v = answersMap[q.question_id];
    return v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && v.length === 0);
  }).length;
  const progress = questions.length ? Math.round((answeredCount / questions.length) * 100) : 0;

  if (loading) {
    return (
      <PageLayout title="Working Style Quiz">
        <div className="text-center py-12 text-slate-500">Loading…</div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Working With Me"
      subtitle="A short, neurodivergent-friendly quiz so your future managers and teammates can understand how you work best. Your answers are private by default."
    >
      <div className="max-w-3xl mx-auto space-y-6" data-testid="working-style-page">
        {/* Status & Progress */}
        <Card className="card-surface print:hidden">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <Rocket className="w-6 h-6 text-[#1E3A8A]" />
                <div>
                  <p className="text-sm font-semibold text-slate-900">{user?.name}'s Working Style</p>
                  <p className="text-xs text-slate-500">{answeredCount} of {questions.length} answered · {progress}%</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  onClick={handlePrint}
                  variant="outline"
                  className="flex items-center gap-2"
                  disabled={!completed}
                  data-testid="print-pdf-btn"
                >
                  <Printer className="w-4 h-4" /> Download PDF
                </Button>
                <Button
                  onClick={handleSave}
                  className="btn-primary flex items-center gap-2"
                  disabled={saving || answeredCount === 0}
                  data-testid="save-quiz-btn"
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                      Saving…
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" /> Save
                    </>
                  )}
                </Button>
              </div>
            </div>
            {/* Progress bar */}
            <div className="mt-4 h-2 rounded-full bg-slate-100 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#FFD507] to-[#1E3A8A] transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>

            {/* Share toggle */}
            <div className="mt-5 flex items-start justify-between gap-4 p-4 rounded-lg bg-slate-50 border border-slate-200">
              <div>
                <Label htmlFor="share-toggle" className="text-sm font-semibold text-slate-900">
                  Show my "Working With Me" card on my Talent DB profile
                </Label>
                <p className="text-xs text-slate-500 mt-1">
                  Off by default. When on, employer partners browsing the Talent DB can see your Working Style summary. Your free-text answers are still private.
                </p>
              </div>
              <Switch
                id="share-toggle"
                checked={sharePublicly}
                onCheckedChange={handleShareToggle}
                data-testid="share-toggle"
              />
            </div>
          </CardContent>
        </Card>

        {/* Questions grouped by category */}
        {categories.map((cat) => {
          const catQs = questions.filter((q) => q.category === cat);
          if (catQs.length === 0) return null;
          return (
            <Card
              key={cat}
              className={`border-2 bg-gradient-to-br ${CATEGORY_COLORS[cat] || "from-slate-100 to-slate-50 border-slate-200"}`}
              data-testid={`category-card-${cat}`}
            >
              <CardHeader>
                <CardTitle className="text-slate-900 text-lg">{cat}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {catQs.map((q) => {
                  const val = answersMap[q.question_id];
                  return (
                    <div key={q.question_id} className="space-y-3" data-testid={`question-${q.question_id}`}>
                      <p className="text-sm font-medium text-slate-900">{q.text}</p>
                      {q.q_type === "mcq" && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {q.options.map((opt) => (
                            <McqOption
                              key={opt}
                              option={opt}
                              selected={val === opt}
                              onChange={() => setAnswer(q.question_id, opt)}
                              testid={`answer-${q.question_id}-${opt.slice(0, 20)}`}
                            />
                          ))}
                        </div>
                      )}
                      {q.q_type === "multi" && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {q.options.map((opt) => (
                            <MultiOption
                              key={opt}
                              option={opt}
                              selected={Array.isArray(val) && val.includes(opt)}
                              onChange={() => toggleMulti(q.question_id, opt)}
                              testid={`answer-${q.question_id}-${opt.slice(0, 20)}`}
                            />
                          ))}
                        </div>
                      )}
                      {q.q_type === "likert" && (
                        <LikertScale
                          value={typeof val === "number" ? val : null}
                          onChange={(n) => setAnswer(q.question_id, n)}
                          questionId={q.question_id}
                        />
                      )}
                      {q.q_type === "text" && (
                        <Textarea
                          value={val || ""}
                          onChange={(e) => setAnswer(q.question_id, e.target.value)}
                          placeholder="Share your thoughts…"
                          className="input-base min-h-[100px]"
                          data-testid={`answer-${q.question_id}`}
                        />
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          );
        })}

        <div className="flex justify-end print:hidden">
          <Button
            onClick={handleSave}
            className="btn-primary flex items-center gap-2"
            disabled={saving || answeredCount === 0}
            data-testid="save-quiz-btn-bottom"
          >
            {saving ? "Saving…" : (<><Save className="w-4 h-4" /> Save my Working Style</>)}
          </Button>
        </div>
      </div>
    </PageLayout>
  );
};

export default WorkingStyleQuiz;
